import { canonicalSubject } from '../../../../utils/subjectLabels';
import { NATIVE_LANG_BY_COUNTRY } from '../../../../utils/subjectUtils';
import { SubjectType } from '../../../_organisms/DesignerTabPane/AgentTab/AgentHooks';

/**
 * Every value the home subject state accepts — the union of both dropdown
 * variants below. `ela` is first because it doubles as the default.
 *
 * Anything outside this list isn't a `SubjectType`, so nothing downstream is
 * typed to handle it.
 */
export const ALL_HOME_SUBJECTS = [
  'ela',
  'english',
  'literacy',
  'math',
  'science',
  'socialStudies',
  'other',
] as const;

export function isValidHomeSubject(value: string): value is SubjectType {
  return (ALL_HOME_SUBJECTS as readonly string[]).includes(value);
}

/**
 * The subject values the home page dropdown actually renders, in render order.
 *
 * `english` and `literacy` are international-only (CURI-301): they exist so
 * teachers in non-native-English countries see "English"/"Literacy" rather than
 * "ELA". A native-English-country teacher is never offered them, and there is no
 * separate RLA value — RLA is a label on `ela` (see the label ladder in
 * WritingPositioningCreateFlow).
 *
 * Kept here rather than inline in the dropdown so the guard that coerces an
 * out-of-list `subject` reads from the same list the `<Select>` renders. If the
 * two drift, the selector goes back to printing a raw value it has no option
 * for, which is the bug this module exists to prevent.
 */
export function homeSubjectValues(isInternational: boolean): SubjectType[] {
  return isInternational
    ? ['english', 'literacy', 'ela', 'socialStudies', 'science', 'math', 'other']
    : ['ela', 'socialStudies', 'science', 'math', 'other'];
}

/**
 * Coerce a subject to something the current dropdown can display.
 *
 * `ela` is the fallback because it is the one option present in every version of
 * the list, and it is the right family for `english`/`literacy` — the card layer
 * already treats all three as ELA.
 *
 * This is deliberately separate from validating against `SUBJECT_OPTIONS` in
 * useHomeSelectorsState. That check rejects values that aren't a `SubjectType`
 * at all (a free-text onboarding answer like "CTE"); this one rejects values
 * that are perfectly valid `SubjectType`s but aren't offered to *this* teacher.
 * Neither catches the other.
 */
export function normalizeHomeSubject(
  subject: string,
  isInternational: boolean,
): SubjectType {
  return homeSubjectValues(isInternational).includes(subject as SubjectType)
    ? (subject as SubjectType)
    : 'ela';
}

/**
 * What the home page's coercion guard should do this render: the subject to
 * switch to, or `null` for "leave it alone".
 *
 * Split out from the effect so the decision is testable without standing up
 * the whole card flow.
 *
 * The two `isInternational` arguments look redundant and are not. The dropdown
 * derives its value from the `country` returned by useHomeLocationState, which
 * is React state refreshed by an effect — so on the render where the access
 * badge first arrives it still holds the 'US' default. Keying the guard off
 * that alone would strip a Norwegian teacher's `english` back to `ela` in that
 * one-render window and persist it, which is the exact CURI-301 regression
 * this guard exists to prevent. So we also compute it straight from the badge
 * and only act when the two agree, which defers the coercion by a render
 * instead of getting it wrong.
 *
 * They can also disagree permanently: with the internal country picker on, a
 * teacher at a US org can be viewing the international experience on purpose.
 * Declining to coerce there is right — the dropdown really is showing them
 * English and Literacy.
 *
 * Note a badge that has loaded but carries no organization still coerces.
 * Both sides read that as non-international, so they agree, and that teacher
 * is shown the native-English option list where `english` is unrenderable.
 */
export function resolveHomeSubjectCoercion({
  hasAccessBadge,
  subject,
  isInternational,
  badgeIsInternational,
}: {
  hasAccessBadge: boolean;
  subject: string;
  /** What the rendered dropdown believes, from the (lagging) `country`. */
  isInternational: boolean;
  /** The same question answered from the access badge, which never lags. */
  badgeIsInternational: boolean;
}): SubjectType | null {
  if (!hasAccessBadge) return null;
  if (isInternational !== badgeIsInternational) return null;
  const normalized = normalizeHomeSubject(subject, isInternational);
  return normalized === subject ? null : normalized;
}

/**
 * Known subject strings, keyed on the lowercased output of `canonicalSubject`,
 * which has already folded every ELA spelling ('English Language Arts', 'ELA',
 * 'RLA', 'Reading Language Arts', …) onto 'ELA' — hence the single `ela` entry.
 */
const KNOWN_SUBJECTS: Record<string, SubjectType> = {
  ela: 'ela',
  english: 'english',
  literacy: 'literacy',
  math: 'math',
  science: 'science',
  'social studies': 'socialStudies',
  other: 'other',
};

/**
 * Turn a human subject string into one of our internal values.
 *
 * Subject strings are free-form and written by three surfaces that never
 * agreed on a spelling: onboarding writes 'English Language Arts' /
 * 'Social Studies', profile settings writes 'ELA' / 'Social studies' /
 * 'World languages', and onboarding's "Other" box writes whatever the teacher
 * typed ('CTE', 'Reading Intervention', …).
 *
 * Three steps, in order:
 *  1. `canonicalSubject` folds the ELA spellings onto one value, so profile
 *     settings' 'ELA' isn't treated as an unknown subject.
 *  2. Case-insensitive lookup, so 'Social studies' matches 'Social Studies'.
 *  3. Anything still unrecognised came from the "Other" free-text box, so it
 *     maps to `other` — the option the teacher actually picked. Dropping it
 *     instead is what made a teacher who typed "CTE" land on Reading Language
 *     Arts: the value was discarded and the ELA default won.
 */
export function resolveSubjectValue(rawSubject: string): SubjectType | undefined {
  const trimmed = rawSubject.trim();
  if (!trimmed) return undefined;
  return KNOWN_SUBJECTS[canonicalSubject(trimmed).toLowerCase()] ?? 'other';
}

/**
 * Which subject to adopt when a teacher ticked several on the onboarding step.
 *
 * Order matters for international teachers: their native-language option is
 * *stored* as 'English Language Arts', so a teacher who picks both "English"
 * and "Norsk" should keep the explicit English choice rather than collapsing
 * to ELA. For native-English countries the distinction is moot — 'English'
 * gets downgraded to `ela` anyway, since english/literacy aren't in their
 * dropdown.
 */
const SUBJECT_PREFERENCE_ORDER = ['English', 'Literacy', 'English Language Arts'];

export function pickPreferredOnboardingSubject(
  subjects: string[] | undefined,
): string | undefined {
  if (!subjects || subjects.length === 0) return undefined;
  return SUBJECT_PREFERENCE_ORDER.find(s => subjects.includes(s)) ?? subjects[0];
}

/**
 * The label the dropdown shows for a value, for this particular teacher.
 *
 * English and Literacy are hardcoded rather than translated so they stay in
 * their canonical form regardless of tolgee locale (CURI-301).
 *
 * SISTER SURFACE: onboarding's subject chip grid lives in OnboardingModal.tsx
 * (`buildInternationalSubjectGroups`). The encoding differs (form-submission
 * strings + React tolgee <T> components there vs. internal SubjectType keys +
 * t() here), and onboarding groups native-lang ELA with the non-recommended
 * chips while the dropdown surfaces it right after Literacy, so the two
 * intentionally aren't extracted — but any new subject added here must also be
 * added there to keep both surfaces in sync.
 */
export function homeSubjectLabel({
  value,
  isInternational,
  treatAsUsTeacher,
  country,
  state,
  isDesktop,
  t,
}: {
  value: SubjectType;
  isInternational: boolean;
  treatAsUsTeacher: boolean;
  country: string;
  state: string;
  isDesktop: boolean;
  t: (key: string, defaultValue: string) => string;
}): string {
  switch (value) {
    case 'english':
      return 'English';
    case 'literacy':
      return 'Literacy';
    case 'socialStudies':
      return t('subject.socialStudies', 'Social Studies');
    case 'science':
      return t('subject.science', 'Science');
    case 'math':
      return t('subject.math', 'Math');
    case 'other':
      return t('subject.other', 'Other');
    case 'ela':
    default: {
      // Four cases, checked in order:
      //   1. International experience (non-native-English country + did NOT
      //      say "yes, intl school") -> native-language label ("Norsk", …).
      //   2. Intl-school teacher (treatAsUsTeacher) -> "Literacy". They're
      //      treated as US teachers everywhere else, but the dropdown reads
      //      "Literacy" to match the school's framing.
      //   3. Texas -> "Reading Language Arts" / "RLA".
      //   4. Default native-English users -> "English Language Arts" / "ELA".
      // Cases 3/4 read as the full name on desktop and collapse to the
      // abbreviation on mobile.
      const nativeLang = country
        ? NATIVE_LANG_BY_COUNTRY[country.toUpperCase()]
        : undefined;
      if (isInternational && nativeLang) return nativeLang;
      if (treatAsUsTeacher) return 'Literacy';
      if (state === 'TX') return isDesktop ? 'Reading Language Arts' : 'RLA';
      return isDesktop
        ? t('subject.ela', 'English Language Arts')
        : t('subject.elaShort', 'ELA');
    }
  }
}
